import type { ApiClient } from '@ghp/api-client'
import { personInitials, type PublicUser } from '@ghp/domain'
import { AppShell, AppShellContent, NavItem, ReportProblem, type ReportProblemProps } from '@ghp/ui'
import { useQuery, type QueryClient } from '@tanstack/react-query'
import {
  Link,
  Outlet,
  createRootRouteWithContext,
  createRoute,
  redirect,
  useNavigate,
  useRouterState,
  type LinkProps,
} from '@tanstack/react-router'
import {
  Archive,
  BellRing,
  MailWarning,
  CalendarDays,
  ClipboardCheck,
  Inbox,
  KeyRound,
  LayoutDashboard,
  FileCog,
  ListChecks,
  LogOut,
  MessageSquareWarning,
  Send,
  Settings,
  ShieldCheck,
  UploadCloud,
  Users,
  UsersRound,
  type LucideIcon,
} from 'lucide-react'
import { decodeAccessToken } from '../../features/auth/access-token'
import { ensureSession, sessionQuery } from '../../features/auth/session'
import { useSessionRenewal } from '../../features/auth/session-renewal'
import { useAuth } from '../../features/auth/use-auth'
import { ROLE_PRESENTATION } from '../../features/users/lib/account-presentation'
import { captureScreen, isErrorReportingEnabled, sendReport } from '../error-reporting'
import s from './root.module.css'

/**
 * Contexte injecté dans toutes les routes.
 *
 * Le `QueryClient` est ce qui permet aux `loader` et aux gardes de lire par le cache
 * (`context.queryClient.ensureQueryData(...)`) — l'intégration Router ↔ Query prévue par
 * **D19/D21** ; la session s'en sert déjà. L'`ApiClient` voyage à côté parce que les
 * `queryFn` en ont besoin **avant** qu'un composant soit monté.
 */
export interface RouterContext {
  queryClient: QueryClient
  api: ApiClient
}

/**
 * La racine rend l'`Outlet` — l'écran de connexion vit hors de la coquille — et, hors de la
 * coquille, le bouton « Signaler un problème » (#481), flottant. Dans la coquille, il est une
 * entrée du bandeau : flottant, il recouvrait le crayon « Modifier » des lignes de tableau.
 */
export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Root,
})

function Root() {
  const inShell = useRouterState({
    select: (state) => state.matches.some((match) => match.routeId === shellRoute.id),
  })

  return (
    <>
      <Outlet />
      {!inShell && <ReportButton />}
    </>
  )
}

function ReportButton({ trigger }: Pick<ReportProblemProps, 'trigger'>) {
  const { queryClient, api } = rootRoute.useRouteContext()
  if (!isErrorReportingEnabled()) return null

  return (
    <ReportProblem
      trigger={trigger}
      capture={captureScreen}
      onSend={(report) => {
        // Lu dans le cache, sans requête : à l'écran de connexion, il n'y a pas de compte.
        const session = queryClient.getQueryData(sessionQuery(api.auth).queryKey)
        const accountId =
          session?.state === 'signed_in' ? decodeAccessToken(session.accessToken)?.sub : undefined
        return sendReport(report, accountId)
      }}
    />
  )
}

/**
 * La coquille et sa garde : toutes les routes de travail vivent dessous.
 *
 * La garde ne lit **rien localement** — contrairement au terrain, dont la session est en
 * IndexedDB (D22). Ici tout tient dans le cookie `httpOnly` (D3) : un `/auth/refresh` dit s'il
 * reste une session, et son échec est une déconnexion, pas un mode dégradé. Un outil de bureau
 * dont le serveur ne répond pas n'a rien à afficher de toute façon — toutes ses listes se
 * lisent en base (D24).
 */
export const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  component: Shell,
  beforeLoad: async ({ context }) => {
    const session = await ensureSession(context.queryClient, context.api.auth)
    if (session.state !== 'signed_in') throw redirect({ to: '/login' })
  },
})

interface SidebarItemProps {
  to: LinkProps['to']
  icon: LucideIcon
  label: string
  /** Pour la route `/` seulement : sans lui, le lien serait actif sur tous les écrans. */
  exact?: boolean
  /** Grisé et non navigable — un écran que le rôle courant ne peut pas ouvrir. */
  disabled?: boolean
  disabledReason?: string
}

/**
 * Un lien du bandeau latéral — le `NavItem` de `@ghp/ui` en orientation horizontale (icône
 * à gauche du libellé), habillant le `Link` de TanStack Router. L'état actif n'est pas un
 * état React : le routeur pose `aria-current="page"` sur le lien actif, et le style s'appuie
 * dessus.
 *
 * Ce qui reste ici : le jeu d'icônes, leur taille, et le routage. L'apparence du lien est
 * partagée avec le rail du terrain.
 */
function SidebarItem({ to, icon: Icon, label, exact, disabled, disabledReason }: SidebarItemProps) {
  return (
    <NavItem
      icon={<Icon size={18} aria-hidden />}
      label={label}
      disabled={disabled}
      disabledReason={disabledReason}
      render={<Link to={to} activeOptions={{ exact }} />}
    />
  )
}

/**
 * La sortie, en pied de bandeau — un `NavItem` habillant un `<button>` plutôt qu'un lien :
 * même géométrie et mêmes couleurs sur fond sombre que la navigation, sans recopier son
 * style ici. Se déconnecter est une **action**, pas une destination : le bouton natif porte
 * le rôle et le clavier.
 *
 * ⚠️ Le reste du bloc utilisateur du prototype (avatar, nom, rôle) n'est pas là : l'API ne dit
 * pas *qui* est connecté — `/auth/refresh` ne rend qu'un jeton (M23). Signalé, pas inventé.
 */
/**
 * « A. Lemoine » — l'initiale du prénom et le nom, comme `.os-uname` du prototype ; le nom seul
 * sans prénom, et le `login` pour un compte sans état civil (M6), le seul champ toujours là.
 */
function displayName(account: PublicUser): string {
  const lastName = account.lastName?.trim() ?? ''
  const firstName = account.firstName?.trim() ?? ''
  if (lastName === '') return account.login

  return firstName === '' ? lastName : `${firstName.charAt(0).toUpperCase()}. ${lastName}`
}

/**
 * Qui est connecté — `.os-user` du prototype, en pied de navigation : pastille d'initiales, nom,
 * rôle. Lu par `GET /me` : le jeton ne porte que l'identifiant et le rôle (M23), et un rechargement
 * de page ne rend que le jeton.
 *
 * ⚠️ **Clé sur l'identifiant du jeton**, pas sur le jeton : il se renouvelle toutes les quinze
 * minutes, et une clé qui le suivrait relirait le compte à chaque fois ; mais un autre compte sur le
 * même poste doit bien relire le sien. Tant que la lecture n'a pas répondu — ou si elle échoue —, le
 * bloc laisse l'écart de la navigation : un bandeau sans nom reste utilisable.
 */
function SidebarUser() {
  const { api } = shellRoute.useRouteContext()
  const { session } = useAuth(api.auth)
  const accessToken = session.state === 'signed_in' ? session.accessToken : null
  const userId = accessToken === null ? null : (decodeAccessToken(accessToken)?.sub ?? null)
  // Générique explicite : le contexte de route type `api` de façon lâche (cf. les autres écrans).
  const me = useQuery<PublicUser>({
    queryKey: ['me', 'profile', userId],
    queryFn: () => api.me.profile(accessToken ?? ''),
    enabled: accessToken !== null,
    staleTime: 5 * 60_000,
  })

  if (me.data === undefined) return null

  return (
    <div className={s.user}>
      <span className={s.avatar} aria-hidden>
        {personInitials(me.data)}
      </span>
      <span>
        <span className={s.userName}>{displayName(me.data)}</span>
        <span className={s.userRole}>{ROLE_PRESENTATION[me.data.role].label}</span>
      </span>
    </div>
  )
}

function SignOut() {
  const { api } = shellRoute.useRouteContext()
  const { signOut } = useAuth(api.auth)
  const navigate = useNavigate()

  return (
    <NavItem
      icon={<LogOut size={18} aria-hidden />}
      label="Se déconnecter"
      render={
        <button
          type="button"
          disabled={signOut.isPending}
          onClick={() =>
            signOut.mutate(undefined, { onSuccess: () => void navigate({ to: '/login' }) })
          }
        />
      }
    />
  )
}

/**
 * Coquille de l'application : le bandeau latéral du prototype validé
 * (`2026-07-21-back-office.html`) — marque, navigation des trois pôles, zone de contenu.
 * Le markup est réécrit, seuls les tokens sont repris (D20).
 */
function Shell() {
  const { api } = shellRoute.useRouteContext()
  const navigate = useNavigate()
  // La garde de route ne rejoue qu'à la navigation : la coquille porte donc aussi le
  // renouvellement du jeton entre deux navigations, et la sortie quand il est refusé.
  useSessionRenewal(api.auth, () => void navigate({ to: '/login' }))

  const { session } = useAuth(api.auth)
  // ⚠️ Lu **seulement** pour griser ou retirer un lien de la nav, jamais pour garder une route :
  // la frontière réelle reste `@Roles('admin')` côté serveur (`users.controller.ts`,
  // `sectors.controller.ts`), qui répondrait `403` à qui contournerait la nav. `undefined` —
  // jeton non décodable, session de test scriptée sans jeton réel — laisse le lien cliquable
  // comme avant cette lecture : en cas de doute, on ne bloque rien de plus que le serveur ne
  // bloque déjà (M21).
  const role =
    session.state === 'signed_in' ? decodeAccessToken(session.accessToken)?.role : undefined
  const isAdmin = role === undefined || role === 'admin'

  // À hauteur de fenêtre, comme le prototype (`#app { height: 100vh; overflow: hidden }`) : le
  // bandeau reste en place avec son propre défilement, et seul le corps de la page défile — sans
  // borne, la page entière défilait et emportait le bandeau avec elle.
  return (
    <AppShell height="viewport">
      <aside className={s.sidebar}>
        <div className={s.top}>
          <span className={s.logo} aria-hidden>
            GHP
          </span>
          <span>
            <span className={s.name}>GHP Pilot</span>
            <span className={s.sub}>Back-office</span>
          </span>
        </div>

        {/* Trois étages, comme le prototype : la marque en tête, la **navigation seule** qui
            défile, et en pied fixe le compte connecté avec la sortie (`.os-user`). */}
        <div className={s.scroll}>
          <nav className={s.nav} aria-label="Navigation principale">
            <span className={s.group}>Pilotage</span>
            <SidebarItem to="/" icon={LayoutDashboard} label="Tableau de bord" exact />
            <SidebarItem to="/inquiries" icon={Inbox} label="Demandes" />
            <SidebarItem to="/planning" icon={CalendarDays} label="Planning" />
            <SidebarItem to="/clients" icon={Users} label="Clients & secteurs" />
            <SidebarItem to="/teams" icon={UsersRound} label="Équipes" />

            <span className={s.group}>Traitement</span>
            <SidebarItem to="/imports" icon={UploadCloud} label="Imports" />
            <SidebarItem to="/inspections" icon={ClipboardCheck} label="Contrôles" />
            <SidebarItem to="/reminders" icon={BellRing} label="Relances" />
            <SidebarItem to="/passage-notices" icon={MailWarning} label="Avis de passage" />
            <SidebarItem to="/exports" icon={Send} label="Export clients" />
            <SidebarItem to="/archives" icon={Archive} label="Archives" />

            <span className={s.group}>Gestion</span>
            {/* En tête du groupe, comme au prototype (`MOD_CATS` d'`ext.js` : « Rapport PDF »
              avant les deux autres) — c'est le réglage qui décide de ce que le client reçoit.
              Renommé le 05/10/2026 (#355) : la même règle décide de ce que la tablette demande. */}
            <SidebarItem to="/report-rules" icon={FileCog} label="Réglage PDF et Contrôle" />
            {/* Deux entrées, comme le prototype validé, et la frontière est le **type de compte** :
              les personnes nommées d'un côté, les comptes partagés des équipes de l'autre. Un
              seul écran obligerait chacun des deux à porter les particularités de l'autre — un
              compte d'équipe n'a pas de titulaire, une personne n'a pas d'initiales de paire. */}
            <SidebarItem
              to="/users"
              icon={ShieldCheck}
              label="Utilisateurs & rôles"
              disabled={!isAdmin}
              disabledReason="Réservé aux administrateurs"
            />
            <SidebarItem
              to="/team-accounts"
              icon={KeyRound}
              label="Comptes équipes"
              disabled={!isAdmin}
              disabledReason="Réservé aux administrateurs"
            />
            <SidebarItem to="/anomaly-rules" icon={ListChecks} label="Règles anomalies" />
            <SidebarItem to="/settings" icon={Settings} label="Réglages" />
          </nav>
        </div>

        <div className={s.foot}>
          <ReportButton
            trigger={({ onClick, disabled }) => (
              <NavItem
                icon={<MessageSquareWarning size={18} aria-hidden />}
                label="Signaler un problème"
                render={<button type="button" disabled={disabled} onClick={onClick} />}
              />
            )}
          />
          <SidebarUser />
          <SignOut />
        </div>
      </aside>

      <AppShellContent>
        <Outlet />
      </AppShellContent>
    </AppShell>
  )
}
